"use client";

import type { StaticImageData } from "next/image";
import type { ReactNode } from "react";
import Image from "next/image";

import { HairlineCardBorder } from "../core/HairlineCardBorder";
import { Noise } from "../core/Noise/Noise";

export interface PricingCardHeaderProps {
  /** Title text */
  title: string;
  /** Optional subtitle (e.g., category, "Popular" badge content) */
  subtitle?: ReactNode;
  /** Formatted price string or custom price element */
  price: ReactNode;
  /** Price unit text or interactive element (e.g., "/month", "per 1K characters", or a dropdown) */
  priceUnit: ReactNode;
  /** Where to place the price unit: inline (same line) or below the price. Defaults to "inline". */
  priceUnitPlacement?: "inline" | "below";
  /** Optional discount message shown above price */
  discountMessage?: ReactNode;
  /** Optional content rendered below the price area inside the header (e.g., "Incl. 10k free credits per month") */
  headerFooter?: ReactNode;
  /** Cover image for the card */
  coverImage?: StaticImageData;
  /** Background color variant */
  backgroundColor?: "dark" | "cream";
  /** Whether this card is highlighted (shows cover image and white text) */
  highlighted?: boolean;
  /** Custom min-height class for the header card. Defaults to "tw-min-h-[12.5rem]". */
  minHeightClassName?: string;
  /** Optional billing period label shown in the bottom-right corner (e.g., "Billed monthly") */
  billingPeriodLabel?: string;
}

/**
 * Shared card header component for pricing and model cards.
 * Handles the visual structure: background, image, noise, border, and content layout.
 */
export const PricingCardHeader = ({
  title,
  subtitle,
  price,
  priceUnit,
  priceUnitPlacement = "inline",
  discountMessage,
  headerFooter,
  coverImage,
  backgroundColor = "dark",
  highlighted = false,
  minHeightClassName = "tw-min-h-[12.5rem]",
  billingPeriodLabel,
}: PricingCardHeaderProps) => {
  const bgClass = "tw-bg-cream";
  const textColorClass = highlighted ? "tw-text-white" : "";

  return (
    <div className={`tw-relative tw-max-w-full tw-overflow-hidden tw-rounded-[1.25rem] ${minHeightClassName}`}>
      {/* Background */}
      <div className={`tw-absolute tw-inset-0 tw-w-full tw-h-full ${bgClass}`} />

      {/* Cover image */}
      {coverImage && (
        <div
          className={`tw-absolute tw-inset-0 tw-w-full tw-h-full tw-overflow-hidden tw-transition-opacity tw-duration-300 ${
            highlighted ? "tw-opacity-100" : "tw-opacity-0"
          }`}
        >
          <Image src={coverImage} alt='' fill className='tw-object-cover' quality={90} />
        </div>
      )}

      {/* Noise overlay */}
      <Noise className='tw-rounded-[1.25rem]' />

      {/* Content */}
      <div className={`tw-relative tw-p-5 tw-flex tw-flex-col tw-justify-between ${minHeightClassName} tw-z-10`}>
        {/* Top section: Title and subtitle */}
        <div>
          <h3 className={`tw-mb-3 tw-transition-colors tw-type-3xl ${textColorClass}`}>{title}</h3>
          {subtitle}
        </div>

        {/* Bottom section: Price */}
        <div className={`tw-transition-colors ${textColorClass}`}>
          {discountMessage}
          <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end sm:tw-justify-between tw-gap-2'>
            <div>
              {priceUnitPlacement === "inline" ? (
                <div className='tw-flex tw-items-end tw-gap-1'>
                  <span className='tw-type-2xl'>{price}</span>
                  <span className='tw-opacity-80 tw-type-xs'>{priceUnit}</span>
                </div>
              ) : (
                <div>
                  <div className='tw-mb-3 tw-type-2xl'>{price}</div>
                  <p className='tw-opacity-80 tw-type-xs'>{priceUnit}</p>
                </div>
              )}
            </div>
            {billingPeriodLabel && <span className='tw-opacity-80 tw-type-2xs'>{billingPeriodLabel}</span>}
          </div>
          {headerFooter}
        </div>
      </div>

      {/* Border */}
      <HairlineCardBorder roundedClassName='tw-rounded-[1.25rem]' />
    </div>
  );
};
